<template>
    <div>
        <vue-slider v-model="value" v-bind="options" :data="data" :marks="marks" :dot-style="dotStyle" :dot-options="dotOptions">
            <template v-slot:dot="{ index }">
                <div v-if="index === 0" class="custom-dot sun-dot">
                    <SunIcon class="w-4 h-4 text-yellow-600" />
                </div>
                <div v-else class="custom-dot moon-dot">
                    <MoonIcon class="w-4 h-4 text-indigo-600" />
                </div>
            </template>
        </vue-slider>
    </div>
</template>

<script>
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import { SunIcon, MoonIcon } from "@heroicons/vue/outline";

export default {
    name: "DayNightModeSlider",
    components: {
        VueSlider,
        SunIcon,
        MoonIcon,
    },
    emits: ["emitChange"],
    props: ["selection"],
    data: function (props) {
        const isMobile = window.innerWidth <= 640;
        return {
            value: props.selection || ["08:00", "20:00"],
            isMobile: isMobile,
            options: {
                process: (pos) => {
                    const segments = [];
                    if (pos[0] > 0) {
                        segments.push([0, pos[0], { backgroundColor: "rgb(88, 80, 236)" }]);
                    }
                    if (pos[1] < 100) {
                        segments.push([pos[1], 100, { backgroundColor: "rgb(88, 80, 236)" }]);
                    }

                    return segments;
                },
                enableCross: true,
                dotSize: isMobile ? 20 : 24,
                height: 4,
                railStyle: {
                    backgroundColor: '#e5e7eb',
                    height: '4px'
                },
                processStyle: {
                    height: '4px'
                }
            },
            dotStyle: {
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none'
            },
            dotOptions: [
                {
                    style: {
                        backgroundColor: '#fef3c7',
                        border: '2px solid #f59e0b',
                        borderRadius: '50%',
                        width: isMobile ? '20px' : '24px',
                        height: isMobile ? '20px' : '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }
                },
                {
                    style: {
                        backgroundColor: '#e0e7ff',
                        border: '2px solid #6366f1',
                        borderRadius: '50%',
                        width: isMobile ? '20px' : '24px',
                        height: isMobile ? '20px' : '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }
                }
            ],
            data: this.generateData(),
            marks: this.generateMarks(isMobile),
        };
    },
    computed: {
        val1() {
            return this.value[0];
        },
    },
    watch: {
        value: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                this.$emit("emitChange", newValue);
            }
        },
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            const newIsMobile = window.innerWidth <= 640;
            if (newIsMobile !== this.isMobile) {
                this.isMobile = newIsMobile;
                this.marks = this.generateMarks(newIsMobile);
                this.options.dotSize = newIsMobile ? 20 : 24;
                this.dotOptions[0].style.width = newIsMobile ? '20px' : '24px';
                this.dotOptions[0].style.height = newIsMobile ? '20px' : '24px';
                this.dotOptions[1].style.width = newIsMobile ? '20px' : '24px';
                this.dotOptions[1].style.height = newIsMobile ? '20px' : '24px';
            }
        },
        generateData() {
            const dataArray = [];

            for (let i = 1; i <= 24 * 4; i++) {
                const totalMinutes = i * 15;
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;

                if (hours === 24 && minutes === 0) {
                    dataArray.push("24:00");
                } else if (hours < 24) {
                    const hoursStr = String(hours).padStart(2, "0");
                    const minutesStr = String(minutes).padStart(2, "0");
                    dataArray.push(`${hoursStr}:${minutesStr}`);
                }
            }

            return dataArray;
        },
        generateMarks(isMobile) {
            const marks = {};
            if (isMobile) {
                for (let i = 1; i <= 23; i += 4) {
                    const time = `${String(i).padStart(2, "0")}:00`;
                    marks[time] = time;
                }
            } else {
                for (let i = 1; i <= 23; i += 2) {
                    const time = `${String(i).padStart(2, "0")}:00`;
                    marks[time] = time;
                }
            }
            return marks;
        },
    },
};
</script>

<style>
.vue-slider-dot-tooltip-top,
.vue-slider-dot-tooltip-inner {
    background-color: rgb(88, 80, 236);
    border-color: rgb(88, 80, 236);
    z-index: 40;
}

.custom-dot {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;
}

.sun-dot {
    background-color: #fef3c7;
    border: 2px solid #f59e0b;
    box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
}

.sun-dot:hover {
    background-color: #fde68a;
    transform: scale(1.1);
}

.moon-dot {
    background-color: #e0e7ff;
    border: 2px solid #6366f1;
    box-shadow: 0 2px 4px rgba(99, 102, 241, 0.3);
}

.moon-dot:hover {
    background-color: #c7d2fe;
    transform: scale(1.1);
}

.vue-slider-mark-label {
    font-size: 12px;
}

@media (max-width: 640px) {
    .vue-slider-mark-label {
        font-size: 10px;
    }
}

.vue-slider-dot {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
}
</style>
